/**
 * AGROMX Frontend - Media Chooser Utility
 *
 * Muestra un overlay in-page para que el usuario elija entre tomar foto/video
 * con la cámara o seleccionar un archivo desde la galería. Necesario porque
 * en Android moderno (>= 13) el Photo Picker no incluye opción de cámara, y
 * por otro lado el atributo capture="environment" abre la cámara directa sin
 * dar acceso a la galería. Con este helper el usuario decide cada vez.
 *
 * Uso típico:
 *   const file = await pickImage();
 *   if (file) ...
 *
 *   const files = await pickImage({ multiple: true });
 *   if (files) ...
 */

function pickMedia({ type = 'image', multiple = false, title } = {}) {
  return new Promise((resolve) => {
    const isVideo = type === 'video';
    const accept = isVideo
      ? 'video/mp4,video/quicktime,video/x-msvideo'
      : 'image/*';
    const heading = title || (isVideo ? 'Agregar Video' : 'Agregar Foto');
    const cameraLabel = isVideo ? '📹 Grabar Video' : '📷 Tomar Foto';
    const galleryLabel = isVideo ? '🖼️ Elegir Video de Galería' : '🖼️ Elegir de Galería';

    const overlay = document.createElement('div');
    overlay.setAttribute('data-media-chooser', '');
    overlay.style.cssText = `
      position: fixed; inset: 0; z-index: 10000;
      background: rgba(0, 0, 0, 0.55);
      display: flex; align-items: center; justify-content: center;
      padding: 1rem;
    `;

    const card = document.createElement('div');
    card.style.cssText = `
      background: white; border-radius: 12px;
      max-width: 380px; width: 100%; padding: 1.5rem;
      box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
      font-family: inherit;
    `;
    card.innerHTML = `
      <h3 style="margin: 0 0 1.25rem; font-size: 1.05rem; text-align: center; color: #1f2937;">${heading}</h3>
      <button data-action="camera" type="button" style="display: block; width: 100%; padding: 0.9rem; margin-bottom: 0.6rem; background: var(--primary, #2563eb); color: white; border: none; border-radius: 8px; font-size: 1rem; font-weight: 600; cursor: pointer;">${cameraLabel}</button>
      <button data-action="gallery" type="button" style="display: block; width: 100%; padding: 0.9rem; margin-bottom: 0.6rem; background: #f3f4f6; color: #1f2937; border: 1px solid #d1d5db; border-radius: 8px; font-size: 1rem; font-weight: 600; cursor: pointer;">${galleryLabel}</button>
      <button data-action="cancel" type="button" style="display: block; width: 100%; padding: 0.7rem; background: transparent; color: #6b7280; border: none; font-size: 0.95rem; cursor: pointer;">Cancelar</button>
    `;
    overlay.appendChild(card);
    document.body.appendChild(overlay);

    let resolved = false;
    const finish = (value) => {
      if (resolved) return;
      resolved = true;
      overlay.remove();
      resolve(value);
    };

    const triggerInput = (useCamera) => {
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = accept;
      if (useCamera) input.capture = 'environment';
      if (multiple && !useCamera) input.multiple = true;
      input.style.display = 'none';
      document.body.appendChild(input);

      const onChange = () => {
        const files = Array.from(input.files || []);
        input.remove();
        if (files.length === 0) {
          finish(null);
        } else if (multiple && !useCamera) {
          finish(files);
        } else {
          finish(files[0]);
        }
      };

      // 'cancel' event soportado en Chrome 113+, Safari 16.4+, Firefox 91+.
      // Si el usuario cierra el picker sin elegir, resolver con null.
      const onCancel = () => {
        input.remove();
        finish(null);
      };

      // Fallback para navegadores que no disparan 'cancel': detectar focus
      // de regreso a la ventana. Se programa diferido para que change/cancel
      // tengan tiempo de dispararse primero.
      const onFocus = () => {
        window.removeEventListener('focus', onFocus);
        setTimeout(() => {
          if (resolved) return;
          if (!input.files || input.files.length === 0) {
            input.remove();
            finish(null);
          }
        }, 500);
      };

      input.addEventListener('change', onChange);
      input.addEventListener('cancel', onCancel);
      window.addEventListener('focus', onFocus);

      input.click();
    };

    card.querySelector('[data-action="camera"]').addEventListener('click', () => triggerInput(true));
    card.querySelector('[data-action="gallery"]').addEventListener('click', () => triggerInput(false));
    card.querySelector('[data-action="cancel"]').addEventListener('click', () => finish(null));
    overlay.addEventListener('click', (e) => {
      if (e.target === overlay) finish(null);
    });
  });
}

export function pickImage(options = {}) {
  return pickMedia({ ...options, type: 'image' });
}

export function pickVideo(options = {}) {
  return pickMedia({ ...options, type: 'video' });
}

export default { pickImage, pickVideo };
